/**
 * Agent / user scope for the two Microsoft Copilot modules.
 *
 * M365 Copilot (/copilot/*) and Copilot Studio (/copilot/studio/*) narrow every
 * list, KPI and aggregate on the server: each request to their microservices
 * accepts `scope_agent` and `scope_user` (comma-separated). The selection lives
 * in the module layout's AgentFilterContext; the layout copies it here while it
 * is mounted (CopilotScopeSync) and clears it on unmount, and the Copilot HTTP
 * clients (services/api/enterpriseAIClient.js) read it for requests to their own
 * prefix only — so nothing reaches another module's requests, and nothing is
 * sent from a page outside the layout.
 *
 * M365 agents are raw `gen_ai.application` values ("IPM.SkypeTeams.Message.
 * Copilot.Outlook"); Studio agents are display names. Values go out verbatim.
 */

export const COPILOT_SCOPE_MODULES = Object.freeze({
  M365: 'copilot',
  STUDIO: 'copilot_studio',
});

const EMPTY = Object.freeze({ agents: Object.freeze([]), users: Object.freeze([]) });
const scopes = new Map();

const clean = (list) => [
  ...new Set((Array.isArray(list) ? list : []).map((v) => String(v ?? '').trim()).filter(Boolean)),
];

/** Replace the module's scope; an empty selection clears it. */
export function setCopilotScope(module, filters) {
  const agents = clean(filters?.agents);
  const users = clean(filters?.users);
  if (!agents.length && !users.length) {
    scopes.delete(module);
    return;
  }
  scopes.set(module, { agents, users });
}

export function clearCopilotScope(module) {
  scopes.delete(module);
}

/** The module's current scope, `{ agents, users }` (empty lists when unset). */
export function getCopilotScope(module) {
  return scopes.get(module) || EMPTY;
}

/** Query params for the module's requests: `{ scope_agent?, scope_user? }`, `{}` when unset. */
export function copilotScopeParams(module) {
  const scope = scopes.get(module);
  if (!scope) return {};
  const params = {};
  if (scope.agents.length) params.scope_agent = scope.agents.join(',');
  if (scope.users.length) params.scope_user = scope.users.join(',');
  return params;
}
